// Mock data generator for the /demo experience. Pure-frontend — no backend
// or Firestore involvement. Same input → same 100 leads each scrape.

import type { Lead, Campaign, GoogleReview } from '@shared/types';

// Stable Unsplash photo IDs grouped by visual theme. Each entry resolves to
// `https://images.unsplash.com/photo-{id}?w=800&q=80&auto=format&fit=crop`.
// Each ID resolves to a real Unsplash photo (verified 200). Pools are kept
// deep so the demo's Sites/Leads tabs show varied imagery without repeats.
const PHOTOS: Record<string, string[]> = {
  trades: [
    '1607472586893-edb57bdc0e39', '1581094271901-8022df4466f9', '1558618666-fcd25c85cd64',
    '1504917595217-d4dc5ebe6122', '1621905251918-48416bd8575a', '1581092160562-40aa08e78837',
    '1556909212-d5b604d0c90d', '1607400201889-565b1ee75f8e', '1530124566582-a618bc2615dc',
    '1503387762-592deb58ef4e',
  ],
  beauty: [
    '1521590832167-7bcbfaa6381f', '1560066984-138dadb4c035', '1522337094846-8a818192de1f',
    '1487412947147-5cebf100ffc2', '1503951914875-452162b0f3f1', '1560869713-7d0a29430803',
    '1595476108010-b4d1f102b1b1', '1571646034647-52e6ea84b28c', '1633681926022-84c23e8cb2d6',
    '1562322140-8baeececf3df', '1559599101-f09722fb4948',
  ],
  food: [
    '1517248135467-4c7edcad34c4', '1466978913421-dad2ebd01d17', '1559339352-11d035aa65de',
    '1555396273-367ea4eb4db5', '1414235077428-338989a2e8c0', '1551218808-94e220e084d2',
    '1552566626-52f8b828add9', '1559925393-8be0ec4767c8', '1424847651672-bf20a4b0982b',
    '1428515613728-6b4607e44363',
  ],
  fitness: [
    '1534438327276-14e5300c3a48', '1571902943202-507ec2618e8f', '1540497077202-7c8a3999166f',
    '1517836357463-d25dfeac3438', '1518611012118-696072aa579a', '1534258936925-c58bed479fcb',
    '1581009146145-b5ef050c2e1e', '1576678927484-cc907957088c', '1583454110551-21f2fa2afe61',
    '1605296867304-46d5465a13f1', '1574680096145-d05b474e2155',
  ],
  professional: [
    '1497366216548-37526070297c', '1556761175-5973dc0f32e7', '1521737604893-d14cc237f11d',
    '1454165804606-c3d57bc86b40', '1573497019940-1c28c88b4f3e', '1486406146926-c627a92ad1ab',
    '1497215728101-856f4ea42174', '1542744173-8e7e53415bb0', '1551836022-d5d88e9218df',
    '1556745757-8d76bdb6984b', '1521791136064-7986c2920216', '1560472354-b33ff0c44a43',
    '1568992687947-868a62a9f521',
  ],
  retail: [
    '1556228720-195a672e8a03', '1504384308090-c894fdcc538d', '1481437156560-3205f6a55735',
    '1607082348824-0a96f2a4b9da', '1604719312566-8912e9227c6a', '1441986300917-64674bd600d8',
    '1472851294608-062f824d29cc', '1567401893414-76b7b1e5a7a5', '1555529669-e69e7aa0ba9a',
    '1542838132-92c53300491e', '1533090161767-e6ffed986c88',
  ],
  outdoor: [
    '1416879595882-3373a0480b5b', '1530541930197-ff16ac917b0e', '1558904541-efa843a96f01',
    '1592417817098-8fd3d9eb14a5', '1558693168-c370615b54e0', '1605000797499-95a51c5269ae',
    '1591857177580-dc82b9ac4e1e',
  ],
};

// Map each campaign niche to a photo theme.
const NICHE_THEMES: Record<string, keyof typeof PHOTOS> = {
  plumber: 'trades', electrician: 'trades', hvac: 'trades', roofing: 'trades',
  landscaping: 'outdoor', cleaning: 'professional', painting: 'trades',
  flooring: 'trades', pest_control: 'trades', locksmith: 'trades',
  garage_door: 'trades', pool_service: 'outdoor', tree_service: 'outdoor',
  moving: 'trades', auto_repair: 'trades',
  dentist: 'professional', chiropractor: 'professional', veterinarian: 'professional',
  salon: 'beauty', spa: 'beauty', gym: 'fitness',
  restaurant: 'food', bakery: 'food', catering: 'food',
  photography: 'professional', real_estate: 'professional', accounting: 'professional',
  lawyer: 'professional', insurance: 'professional', daycare: 'professional',
  tutoring: 'professional', pet_grooming: 'beauty',
  florist: 'retail', tailor: 'retail',
};

// Niche-appropriate "service offerings" used in the templated generated site.
export const NICHE_SERVICES: Record<string, string[]> = {
  plumber: ['Emergency repairs', 'Drain cleaning', 'Water heater install', 'Leak detection'],
  electrician: ['Wiring & rewiring', 'Panel upgrades', 'Lighting install', 'EV charger install'],
  hvac: ['AC repair', 'Furnace install', 'Duct cleaning', 'Thermostat upgrades'],
  roofing: ['Roof replacement', 'Storm damage repair', 'Gutter install', 'Inspections'],
  landscaping: ['Lawn care', 'Garden design', 'Hardscaping', 'Tree trimming'],
  cleaning: ['Deep cleans', 'Recurring service', 'Move-out cleaning', 'Window washing'],
  painting: ['Interior painting', 'Exterior painting', 'Cabinet refinishing', 'Drywall repair'],
  flooring: ['Hardwood install', 'Tile & stone', 'Carpet replacement', 'Refinishing'],
  pest_control: ['Termite treatment', 'Rodent control', 'Bed bug removal', 'Quarterly service'],
  locksmith: ['Lockouts', 'Rekeying', 'Smart lock install', '24/7 emergency'],
  garage_door: ['Spring repair', 'Opener install', 'Panel replacement', 'Maintenance'],
  pool_service: ['Weekly cleaning', 'Chemical balancing', 'Equipment repair', 'Opening & closing'],
  tree_service: ['Tree removal', 'Stump grinding', 'Trimming & pruning', 'Emergency service'],
  moving: ['Local moves', 'Long-distance moves', 'Packing service', 'Storage'],
  auto_repair: ['Oil changes', 'Brake service', 'Engine diagnostics', 'Transmission repair'],
  dentist: ['Cleanings & exams', 'Whitening', 'Crowns & bridges', 'Emergency dental'],
  chiropractor: ['Spinal adjustments', 'Sports injury care', 'Massage therapy', 'Pain management'],
  veterinarian: ['Wellness exams', 'Vaccinations', 'Surgery', 'Dental cleanings'],
  salon: ['Cuts & styles', 'Color & highlights', 'Extensions', 'Bridal styling'],
  spa: ['Massage', 'Facials', 'Body treatments', 'Couples packages'],
  gym: ['Personal training', 'Group classes', 'Strength equipment', 'Cardio zone'],
  restaurant: ['Lunch menu', 'Dinner menu', 'Catering', 'Private events'],
  bakery: ['Custom cakes', 'Daily breads', 'Pastries', 'Catering'],
  photography: ['Weddings', 'Portraits', 'Events', 'Commercial shoots'],
  real_estate: ['Buyer representation', 'Listing service', 'Market analysis', 'Investment properties'],
  accounting: ['Tax prep', 'Bookkeeping', 'Payroll', 'Financial planning'],
  lawyer: ['Consultations', 'Contract review', 'Litigation', 'Estate planning'],
  insurance: ['Auto', 'Home', 'Life', 'Business'],
  daycare: ['Infant care', 'Toddler programs', 'Pre-K', 'After-school'],
  tutoring: ['Math', 'Reading', 'Test prep', 'College essays'],
  pet_grooming: ['Full grooms', 'Bath & brush', 'Nail trims', 'De-shedding'],
  catering: ['Corporate lunch', 'Weddings', 'Drop-off catering', 'Bar service'],
  florist: ['Bouquets', 'Wedding flowers', 'Same-day delivery', 'Subscriptions'],
  tailor: ['Suit alterations', 'Hemming', 'Bridal alterations', 'Repairs'],
};

// Niche-appropriate business name fragments.
const NAME_PARTS: Record<string, { prefixes: string[]; suffixes: string[] }> = {
  plumber: { prefixes: ['Reliable', 'Premier', "Joe's", 'Anchor', 'Patriot', "Mike's", 'Diamond', 'Riverside'], suffixes: ['Plumbing', 'Plumbing & Drain', 'Plumbing Co.', 'Plumbing Services'] },
  electrician: { prefixes: ['Sparks', 'Voltage', 'Bright', 'Lightning', 'Power', 'Coastal', 'Summit'], suffixes: ['Electric', 'Electrical', 'Electrical Services', 'Electric Co.'] },
  hvac: { prefixes: ['Cool Breeze', 'Air Pros', 'Comfort', 'Climate', 'Polar', 'Summit', 'Liberty'], suffixes: ['HVAC', 'Heating & Air', 'Climate Solutions', 'Air Conditioning'] },
  roofing: { prefixes: ['Sky High', 'Apex', 'Summit', 'Iron Clad', 'Northern', 'Patriot'], suffixes: ['Roofing', 'Roofing Co.', 'Roof Pros', 'Roofing & Construction'] },
  landscaping: { prefixes: ['Green Thumb', 'Evergreen', 'Sunset', 'Meadow', 'Cedar', 'Oakwood'], suffixes: ['Landscaping', 'Lawn Care', 'Outdoor', 'Lawn & Garden'] },
  cleaning: { prefixes: ['Sparkle', 'Crystal', 'Pristine', 'Maid', 'Fresh', 'Spotless', 'Sunshine'], suffixes: ['Cleaning', 'Cleaners', 'Cleaning Co.', 'Cleaning Service'] },
  painting: { prefixes: ['True Color', 'Brushstrokes', 'Premier', "Tom's", 'Brighter', 'Heritage'], suffixes: ['Painting', 'Painters', 'Painting Co.', 'Paint Pros'] },
  flooring: { prefixes: ['Solid', 'Hardwood', 'Premier', 'Modern', "Ben's"], suffixes: ['Floors', 'Flooring', 'Flooring & Tile', 'Floor Co.'] },
  pest_control: { prefixes: ['Bug Out', 'Pest Pros', 'Reliable', 'Liberty', 'Sentinel'], suffixes: ['Pest Control', 'Pest Solutions', 'Exterminators', 'Pest Services'] },
  locksmith: { prefixes: ['24/7', 'Mobile', 'Pro', 'Citywide', 'Reliable'], suffixes: ['Locksmith', 'Lock & Key', 'Lock Service', 'Locks'] },
  garage_door: { prefixes: ['Premier', 'Garage Pros', 'Reliable', "Tony's", 'Modern'], suffixes: ['Garage Doors', 'Door Service', 'Garage Door Repair'] },
  pool_service: { prefixes: ['Crystal', 'Aqua', 'Blue Wave', 'Sunset', 'Bayside'], suffixes: ['Pools', 'Pool Service', 'Pool & Spa', 'Pool Care'] },
  tree_service: { prefixes: ['Oakwood', 'Evergreen', 'Treetop', 'Heritage', 'Cedar'], suffixes: ['Tree Service', 'Tree Co.', 'Tree Care', 'Arbor'] },
  moving: { prefixes: ['Two Guys', 'Premier', "Joe's", 'Reliable', 'Patriot'], suffixes: ['Moving', 'Movers', 'Moving Co.', 'Moving & Storage'] },
  auto_repair: { prefixes: ['Honest', "Mike's", 'Premier', 'Family', 'Heritage'], suffixes: ['Auto', 'Auto Repair', 'Automotive', 'Garage'] },
  dentist: { prefixes: ['Smile', 'Bright', 'Family', 'Modern', 'Sunset'], suffixes: ['Dental', 'Dentistry', 'Dental Care', 'Family Dentistry'] },
  chiropractor: { prefixes: ['Active Life', 'Spinal', 'Wellness', 'Premier', 'Family'], suffixes: ['Chiropractic', 'Chiro', 'Spine & Wellness'] },
  veterinarian: { prefixes: ['Family', 'Companion', 'Bayside', 'Riverside', 'Furry Friends'], suffixes: ['Animal Hospital', 'Veterinary Clinic', 'Pet Care', 'Vet'] },
  salon: { prefixes: ['Bella', 'Sage', 'Mane', 'Polished', 'Sunset', 'Lush'], suffixes: ['Hair Studio', 'Salon', 'Hair Co.', 'Beauty Bar'] },
  spa: { prefixes: ['Serenity', 'Lotus', 'Pure', 'Tranquil', 'Bliss'], suffixes: ['Spa', 'Day Spa', 'Wellness Spa', 'Spa & Wellness'] },
  gym: { prefixes: ['Iron', 'Peak', 'Strong', 'Pulse', 'Forge', 'Apex'], suffixes: ['Fitness', 'Gym', 'Strength & Conditioning', 'Athletics'] },
  restaurant: { prefixes: ['The Local', 'Sage', "Mama's", 'Riverside', 'Heritage', 'Sunset'], suffixes: ['Kitchen', 'Bistro', 'Grill', 'Tavern', 'Eatery'] },
  bakery: { prefixes: ['Sweet', 'Sunrise', 'Flour', 'Heritage', "Mama's"], suffixes: ['Bakery', 'Bake Shop', 'Cakes & Pastries', 'Bread Co.'] },
  photography: { prefixes: ['Lens', 'Light & Frame', 'Riverside', 'True Story', 'Golden Hour'], suffixes: ['Photography', 'Photo Studio', 'Visuals', 'Photo Co.'] },
  real_estate: { prefixes: ['Hometown', 'Premier', 'Coastal', 'Heritage', 'Summit'], suffixes: ['Realty', 'Real Estate', 'Properties', 'Realty Group'] },
  accounting: { prefixes: ['Liberty', 'Precision', 'Heritage', 'Summit', 'Patriot'], suffixes: ['CPA', 'Accounting', 'Tax & Accounting', 'Financial'] },
  lawyer: { prefixes: ['Liberty', 'Heritage', 'Summit', 'Patriot', 'Cornerstone'], suffixes: ['Law', 'Law Firm', 'Legal', 'Attorneys at Law'] },
  insurance: { prefixes: ['Liberty', 'Heritage', 'Patriot', 'Premier', 'Family'], suffixes: ['Insurance', 'Insurance Group', 'Coverage', 'Insurance Co.'] },
  daycare: { prefixes: ['Little Stars', 'Bright Beginnings', 'Sunshine', 'Tiny Tots', 'Rainbow'], suffixes: ['Daycare', 'Childcare', 'Learning Center', 'Kids Academy'] },
  tutoring: { prefixes: ['Bright Minds', 'Achieve', 'Premier', 'Summit', 'Excel'], suffixes: ['Tutoring', 'Learning', 'Academic', 'Tutors'] },
  pet_grooming: { prefixes: ['Furry Friends', 'Pampered', 'Sage', 'Lucky Paws', 'Bayside'], suffixes: ['Grooming', 'Pet Spa', 'Dog Grooming', 'Pet Care'] },
  catering: { prefixes: ['Heritage', "Mama's", 'Sage', 'Sunset', 'Riverside'], suffixes: ['Catering', 'Catering Co.', 'Events & Catering', 'Kitchen'] },
  florist: { prefixes: ['Bloom', 'Petal', 'Sage', 'Sunset', 'Wildflower'], suffixes: ['Florist', 'Floral', 'Flowers', 'Flower Shop'] },
  tailor: { prefixes: ['Master', 'Heritage', 'Old World', 'Premier', 'Golden Needle'], suffixes: ['Tailor', 'Alterations', 'Tailoring', 'Custom Clothing'] },
};

const LOCAL_AREAS = [
  'Downtown', 'East Side', 'West End', 'North End', 'Southside',
  'Heights', 'Riverside', 'Lakeside', 'Old Town', 'Midtown',
  'Uptown', 'Westgate', 'Eastgate', 'Highland', 'Brookside',
  'Fairview', 'Maple District', 'Cedar Park', 'Bayview', 'Hillcrest',
];

// Real cities per country, keyed by the country *name* stored on a non-local
// campaign (see DemoCampaigns COUNTRIES). A country-wide scrape spreads its
// leads across these so each result reads as a real, distinct place rather
// than a generic "United States".
const COUNTRY_CITIES: Record<string, string[]> = {
  'United States': [
    'New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX', 'Phoenix, AZ',
    'Miami, FL', 'Seattle, WA', 'Denver, CO', 'Atlanta, GA', 'Boston, MA',
    'Nashville, TN', 'Portland, OR', 'Dallas, TX', 'San Diego, CA', 'Charlotte, NC',
    'Minneapolis, MN',
  ],
  'United Kingdom': [
    'London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow',
    'Liverpool', 'Bristol', 'Edinburgh', 'Sheffield', 'Newcastle',
  ],
  'Canada': [
    'Toronto, ON', 'Vancouver, BC', 'Montreal, QC', 'Calgary, AB', 'Ottawa, ON',
    'Edmonton, AB', 'Winnipeg, MB', 'Halifax, NS',
  ],
  'Australia': [
    'Sydney, NSW', 'Melbourne, VIC', 'Brisbane, QLD', 'Perth, WA', 'Adelaide, SA',
    'Gold Coast, QLD', 'Canberra, ACT', 'Wollongong, NSW',
  ],
};

const REVIEWERS = [
  'Sarah K.', 'Mike R.', 'Jennifer L.', 'David P.', 'Amanda H.',
  'Chris M.', 'Linda T.', 'James B.', 'Patricia W.', 'Robert S.',
  'Maria G.', 'John D.', 'Lisa F.', 'Steven A.', 'Karen N.',
];

const REVIEW_TEMPLATES = [
  'Best {niche} we have ever used. {owner} was on time, fair priced, and got the job done right.',
  'Couldn\'t be happier with the service. Honest, professional, and reasonably priced. Highly recommend.',
  'Showed up when promised and did exactly what they said they would do. Will definitely use again.',
  'Top notch work and great communication. Took the time to explain everything. 10/10.',
  'Great experience start to finish. The team was friendly and the quality of work was excellent.',
  'Hands down the best in town. Already recommended to three of my neighbors.',
  'Professional, knowledgeable, and stood behind their work. That\'s rare these days.',
];

// Deterministic pseudo-random based on a string seed.
function hash(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h * 16777619) >>> 0;
  }
  return h;
}

function pick<T>(arr: T[], seed: number): T {
  return arr[seed % arr.length];
}

function photoUrl(id: string, w = 800): string {
  return `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop`;
}

export function getPhotosForNiche(niche: string, leadIdx: number): string[] {
  const theme = NICHE_THEMES[niche] || 'professional';
  const pool = PHOTOS[theme];
  // Hash-based start offset so congruent lead indices (e.g. 3 and 8 in a
  // 5-deep pool both gave offset 3 under the old `leadIdx % len`) and
  // different niches sharing a theme no longer collide on the same photo.
  // Stride 3 is coprime to every pool length here, so the 3 picks are
  // always distinct and visually spread apart.
  const offset = hash(`${niche}-photo-${leadIdx}`) % pool.length;
  const stride = 3;
  return [
    photoUrl(pool[offset]),
    photoUrl(pool[(offset + stride) % pool.length]),
    photoUrl(pool[(offset + 2 * stride) % pool.length]),
  ];
}

// Build a UNIQUE business name per lead index within a campaign. The previous
// version picked prefix and suffix independently from small pools, so with
// only ~8×4 combinations duplicates were guaranteed across a 100-lead scrape
// ("Reliable Plumbing" appearing several times). Here we walk the full
// prefix×suffix grid as a bijection of the sequential index, so the first
// P×S leads are all distinct. Once the grid is exhausted we wrap and qualify
// the name with a neighborhood ("Reliable Plumbing of Heights"), keeping every
// name unique for up to 100 leads. A per-campaign offset keeps two campaigns
// of the same niche from producing identical name sequences.
function generateBusinessName(niche: string, i: number, campaignId: string): string {
  const parts = NAME_PARTS[niche] || NAME_PARTS.plumber;
  const P = parts.prefixes.length;
  const S = parts.suffixes.length;
  const combos = P * S;
  const offset = hash(`${campaignId}-${niche}-name`) % combos;
  const n = offset + i;
  const prefix = parts.prefixes[n % P];
  const suffix = parts.suffixes[Math.floor(n / P) % S];
  const base = `${prefix} ${suffix}`;
  const round = Math.floor(n / combos);
  if (round === 0) return base;
  return `${base} of ${LOCAL_AREAS[(round - 1) % LOCAL_AREAS.length]}`;
}

function generatePhone(idx: number): string {
  const seed = hash(`phone-${idx}`);
  const area = 200 + (seed % 800);
  const exch = 200 + ((seed >>> 8) % 800);
  const num = 1000 + ((seed >>> 16) % 9000);
  return `(${area}) ${exch}-${num}`;
}

function generateEmail(name: string): string {
  const slug = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  const domains = ['gmail.com', 'yahoo.com', `${slug}.com`, 'outlook.com'];
  const idx = hash(`${name}-email`) % domains.length;
  return `info@${domains[idx]}`;
}

export function generateReviews(businessName: string, niche: string, count: number): GoogleReview[] {
  const ownerFirstName = businessName.split(' ')[0].replace(/'s$/, '');
  return Array.from({ length: count }).map((_, i): GoogleReview => {
    const seed = hash(`${businessName}-review-${i}`);
    const text = pick(REVIEW_TEMPLATES, seed)
      .replace('{niche}', niche.replace(/_/g, ' '))
      .replace('{owner}', ownerFirstName);
    const rating = 4 + (seed % 2); // 4 or 5 stars
    return {
      authorName: pick(REVIEWERS, seed),
      rating,
      text,
      relativeTime: pick(['1 week ago', '2 weeks ago', '1 month ago', '2 months ago', '3 months ago'], seed),
    };
  });
}

export interface DemoCampaign extends Omit<Campaign, 'createdAt' | 'lastScrapedAt'> {
  id: string;
  isLocalCampaign?: boolean;
  createdAt: Date;
  lastScrapedAt?: Date;
}

export interface DemoLead extends Omit<Lead, 'createdAt' | 'googleReviews' | 'businessImages'> {
  id: string;
  createdAt: Date;
  googleReviews: GoogleReview[];
  businessImages: string[];
}

// Generate a deterministic batch of leads for a given campaign. Same campaign
// (id + niche + location + maxLeads) always yields the same list — refreshing
// is fine because state is in memory only.
export function generateMockLeads(campaign: DemoCampaign): DemoLead[] {
  const count = Math.min(campaign.maxLeads ?? 100, 100);
  const niche = campaign.niche === 'any'
    ? Object.keys(NAME_PARTS)[hash(campaign.id) % Object.keys(NAME_PARTS).length]
    : campaign.niche;

  const baseLocation = campaign.location;

  // A country-wide campaign stores a country name (e.g. "United States"); its
  // leads spread across real cities in that country. Any other campaign stores
  // a city (local toggle, or a seeded "Austin, TX" campaign), so its leads get
  // a neighborhood within that city. Either way every lead lands at a concrete,
  // varied place that matches the location set on the campaign — and because
  // the generated site is built from lead.location, the site reflects it too.
  const cityPool = COUNTRY_CITIES[baseLocation];

  return Array.from({ length: count }).map((_, i): DemoLead => {
    const businessName = generateBusinessName(niche, i, campaign.id);
    const seed = hash(`${campaign.id}-${i}`);
    const rating = +(3.8 + (seed % 13) / 10).toFixed(1); // 3.8 - 5.0
    const reviewCount = 12 + (seed % 240);
    const reviewSampleCount = 3 + (seed % 3); // 3-5 sample reviews
    // Stride 7 is coprime to 20, so the first 20 leads get distinct areas.
    const areaIdx = (hash(`${campaign.id}-area`) + i * 7) % LOCAL_AREAS.length;
    const location = cityPool
      ? cityPool[hash(`${campaign.id}-city-${i}`) % cityPool.length]
      : `${LOCAL_AREAS[areaIdx]}, ${baseLocation}`;

    // /demo always shows leads as needing a website — the whole pitch of the
    // demo is "look at all these prospects with no web presence."
    const hasWebsite = false;
    const hasEmail = (seed % 3) !== 0;   // ~67% have email
    const phone = generatePhone(seed);

    return {
      id: `demo-lead-${campaign.id}-${i}`,
      userId: 'demo-user',
      campaignId: campaign.id,
      businessName,
      niche,
      location,
      contactMethod: hasEmail ? 'email' : 'sms',
      email: hasEmail ? generateEmail(businessName) : null,
      emailConfidence: hasEmail ? 0.85 : null,
      emailSource: hasEmail ? 'pattern' : null,
      phone,
      phoneNormalized: phone.replace(/\D/g, ''),
      smsCapable: true,
      rating,
      reviewCount,
      hasWebsite,
      siteGenerated: false,
      googlePlaceId: `demo-place-${campaign.id}-${i}`,
      businessImages: getPhotosForNiche(niche, i),
      googleReviews: generateReviews(businessName, niche, reviewSampleCount),
      businessHours: ['Mon-Fri 8am-6pm', 'Sat 9am-4pm', 'Sun Closed'],
      googleMapsUrl: `https://maps.google.com/?q=${encodeURIComponent(businessName + ' ' + location)}`,
      status: 'new',
      createdAt: new Date(Date.now() - (i * 60_000)),
    };
  });
}
